import * as THREE from 'three'
import Experience from '../Experience.js'
import supabase   from '../../utils/supabase.js'

const FORMATS = {
    '9x16': { width: 1080, height: 1920 },
    '16x9': { width: 1920, height: 1080 },
    '1x1':  { width: 1080, height: 1080 }
}

const MIME_PRIORITY = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm'
]

export default class VideoExporter {
    constructor() {
        this.experience = new Experience()
        this.isRecording = false

        this._offRenderer  = null
        this._offCamera    = null
        this._offCanvas    = null
        this._compCanvas   = null
        this._compCtx      = null
        this._recorder     = null
        this._chunks       = []
        this._tickHandler  = null
        this._timerID      = null
        this._elapsed      = 0
        this._audioDest    = null
        this._mimeType     = null
        this._offBg        = null

        this._prewarmFormat  = null
        this._exportSession  = null  // set by checkExportAllowed, read by _finalize

        this._recIndicator = document.getElementById('recIndicator')
        this._recTimeEl    = document.getElementById('recTime')
        this._stopBtn      = document.getElementById('stopRecBtn')

        this._stopBtn.addEventListener('click', () => this.stopRecording())
    }

    // Call on touchstart of export buttons so shader compilation overlaps with
    // the Turnstile challenge (2–5 s) instead of blocking the start of recording.
    prewarm(format) {
        if (this.isRecording) return
        if (this._offRenderer && this._prewarmFormat === format) return

        if (this._offRenderer) {
            this._offRenderer.dispose()
            this._offCanvas?.remove()
            this._offRenderer = null
            this._offCanvas   = null
            this._offCamera   = null
        }

        const { width, height } = FORMATS[format] || FORMATS['16x9']

        this._offCanvas = document.createElement('canvas')
        this._offCanvas.width  = width
        this._offCanvas.height = height
        this._offCanvas.style.cssText = 'position:fixed;left:-9999px;top:0'
        document.body.appendChild(this._offCanvas)

        this._offRenderer = new THREE.WebGLRenderer({
            canvas: this._offCanvas,
            antialias: true,
            preserveDrawingBuffer: true
        })
        this._offRenderer.setSize(width, height)
        this._offRenderer.setPixelRatio(2)

        this._offCamera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100)
        this._offCamera.position.set(0, 0, 2.0)

        // Compile shaders on prewarm so it overlaps with Turnstile challenge.
        this._offRenderer.compile(this.experience.scene, this._offCamera)
        this._prewarmFormat = format
    }

    // ── Session check — called BEFORE Turnstile ──────────────────────────────
    async checkExportAllowed(format) {
        const { data: { session } } = await supabase.auth.getSession()
        return { allowed: true, session: session ?? null }
    }

    startRecording(format) {
        if (this.isRecording) return
        const { width, height } = FORMATS[format] || FORMATS['16x9']

        const analyser = this.experience.world.audioAnalyser

        // Reuse prewarmed offscreen renderer if format matches — zero compile cost.
        // Fall back to inline prewarm() if touchstart didn't fire (desktop click).
        if (!this._offRenderer || this._prewarmFormat !== format) this.prewarm(format)

        // Background scale for offscreen viewport — derive from actual offCamZ, not hard-coded 2.0.
        const fovRad = 35 * Math.PI / 180
        const bgH    = 2 * Math.tan(fovRad / 2) * this._offCamera.position.z
        const bgW    = bgH * (width / height)
        this._offBg  = { scaleX: bgW / 2, scaleY: bgH / 2, resX: width, resY: height }

        // ── Composite 2-D canvas (WebGL frame + watermark → MediaRecorder) ────
        this._compCanvas = document.createElement('canvas')
        this._compCanvas.width  = width
        this._compCanvas.height = height
        this._compCanvas.style.cssText = 'position:fixed;left:-9998px;top:0'
        document.body.appendChild(this._compCanvas)
        this._compCtx = this._compCanvas.getContext('2d')

        // ── Tick: render WebGL → copy to 2D canvas → draw watermark ──────────
        const ps       = this.experience.world.particleSystem
        const liveZ    = this.experience.camera.instance.position.z
        const offZ     = this._offCamera.position.z
        const sizeScale = (offZ / liveZ) * 0.60  // compensate: off-camera is farther → 1/-z shrinks points

        this._tickHandler = () => {
            const bg = this.experience.world.background

            // Scale uSize so particles appear the same visual size despite off-camera being at z=2.
            // Shader: gl_PointSize *= (1 / -viewZ). At z=2 that's ×0.5, so we pre-multiply by offZ/liveZ.
            const uSize = ps?.material?.uniforms?.uSize
            const savedSize = uSize?.value
            if (uSize) uSize.value = savedSize * sizeScale

            if (bg) {
                const sx = bg.mesh.scale.x, sy = bg.mesh.scale.y
                const rx = bg.material.uniforms.uResolution.value.x
                const ry = bg.material.uniforms.uResolution.value.y

                bg.mesh.scale.set(this._offBg.scaleX, this._offBg.scaleY, 1)
                bg.material.uniforms.uResolution.value.set(this._offBg.resX, this._offBg.resY)
                this._offRenderer.render(this.experience.scene, this._offCamera)
                bg.mesh.scale.set(sx, sy, 1)
                bg.material.uniforms.uResolution.value.set(rx, ry)
            } else {
                this._offRenderer.render(this.experience.scene, this._offCamera)
            }

            if (uSize) uSize.value = savedSize

            this._compCtx.drawImage(this._offCanvas, 0, 0)
        }
        this.experience.time.on('tick.videoExporter', this._tickHandler)

        // ── Video stream from composite canvas ────────────────────────────────
        const videoStream = this._compCanvas.captureStream(30)

        let combinedStream = videoStream
        if (analyser._ctx && analyser._analyser) {
            this._audioDest = analyser._ctx.createMediaStreamDestination()
            analyser._analyser.connect(this._audioDest)
            combinedStream = new MediaStream([
                ...videoStream.getTracks(),
                ...this._audioDest.stream.getTracks()
            ])
        }

        this._mimeType = MIME_PRIORITY.find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm'

        this._recPresetId = document.querySelector('.pill.active[data-preset]')?.dataset.preset || 'void'

        this._chunks = []
        this._recorder = new MediaRecorder(combinedStream, {
            mimeType: this._mimeType,
            videoBitsPerSecond: 8_000_000
        })
        this._recorder.ondataavailable = (e) => { if (e.data.size > 0) this._chunks.push(e.data) }
        this._recorder.onstop = () => this._finalize(format)
        this._recorder.start(100)

        this.isRecording = true
        this._elapsed    = 0
        this._recIndicator.classList.add('active')
        this._timerID = setInterval(() => {
            this._elapsed++
            const m = Math.floor(this._elapsed / 60)
            const s = String(this._elapsed % 60).padStart(2, '0')
            this._recTimeEl.textContent = `${m}:${s}`
        }, 1000)
    }

    _drawWatermark(ctx, width, height) {
        const t  = this.experience.time.elapsed * 0.001
        // Slow lissajous drift — stays within 15%–85% of each dimension
        const px = 0.5 + 0.30 * Math.sin(t * 0.023)
        const py = 0.5 + 0.35 * Math.cos(t * 0.017)
        const opacity  = 0.20 + 0.08 * Math.sin(t * 0.11)
        const fontSize = Math.max(18, Math.round(width * 0.021))

        ctx.save()
        ctx.font = `400 ${fontSize}px 'Tenor Sans', sans-serif`
        if ('letterSpacing' in ctx) ctx.letterSpacing = '3px'
        ctx.textAlign    = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle    = `rgba(237,186,151,${opacity.toFixed(3)})`
        ctx.fillText('vyzflow.com', px * width, py * height)
        ctx.restore()
    }

    stopRecording() {
        if (!this.isRecording) return
        this._recorder.stop()
        this._cleanup()
    }

    _cleanup() {
        try {
            clearInterval(this._timerID)
            this._timerID = null

            this.experience.time.off('tick.videoExporter')
            this._tickHandler = null

            if (this._audioDest) {
                this.experience.world.audioAnalyser._analyser?.disconnect(this._audioDest)
                this._audioDest = null
            }

            this._offRenderer?.dispose()
            this._offCanvas?.remove()
            this._compCanvas?.remove()

            this._offRenderer = null
            this._offCanvas   = null
            this._compCanvas  = null
            this._compCtx     = null
            this._offCamera   = null
            this._offBg       = null
        } finally {
            this.isRecording = false
            this._prewarmFormat = null
            this._recIndicator.classList.remove('active')
            this._recTimeEl.textContent = '0:00'
        }
    }
    async _finalize(format) {
        const ext    = this._mimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
        const preset = this._recPresetId || 'void'
        const ts     = Date.now()
        const name   = `vyzflow-${preset}-${format}-${ts}.${ext}`

        const blob = new Blob(this._chunks, { type: this._mimeType })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = name
        a.click()
        URL.revokeObjectURL(url)
        this._chunks = []

        // Log export to Supabase (non-blocking — user already has the file)
        if (this._exportSession) {
            const { error } = await supabase.from('exports').insert({
                user_id:   this._exportSession.user.id,
                preset_id: preset,
                format
            })
            if (error) console.warn('export log failed:', error)
            this._exportSession = null
        }
    }
}
